import { List } from '@mui/material';
import isEqual from 'lodash.isequal';
import React, { memo } from 'react';
import { GlobalFilterTypeHandle } from '../../../../context/types';
import { FilterTypes } from '../filterbar/types';
import { HierarchyItem } from '../types';
import { isChecked } from '../utils';
import { FilterTrayItem } from './FilterTrayItem';
import { SelectAllClearAllButtonGroup } from './SelectAllClearAllButtonGroup';

interface FilterTrayItemsProps<T extends FilterTypes> {
  items: HierarchyItem[];
  filterHandle: GlobalFilterTypeHandle<T>;
  forceOpen: boolean;
}

export const FilterTrayItems = <T extends FilterTypes>(
  props: FilterTrayItemsProps<T>
) => {
  const { items, filterHandle, forceOpen } = props;
  return (
    <List
      component={'div' as 'ul'}
      sx={{ padding: '0 0 0 12px', backgroundColor: `rgba(0,0,0,${1 * 0.15})` }}
      dense
    >
      {items[0]?.isSelectable ? (
        <SelectAllClearAllButtonGroup
          subItems={items}
          filterHandle={filterHandle}
          level={0}
        />
      ) : null}
      {items.map((subItem) => {
        return (
          <FilterTrayItem<T>
            key={`filter-tray-item-${subItem.key.hash}-${0}-${subItem.itemId ?? ''}`}
            level={1}
            parentItem={subItem}
            filterHandle={filterHandle}
            forceOpen={forceOpen}
          />
        );
      })}
    </List>
  );
};
FilterTrayItems.displayName = 'FilterTrayItems';

type CheckedHierarchy = {
  parent: boolean;
  subItems: CheckedHierarchy[];
};

const isCheckedRecursively = <T extends FilterTypes>(
  item: HierarchyItem,
  selectedFilters: T[]
): CheckedHierarchy => {
  return {
    parent: isChecked(item, selectedFilters),
    subItems:
      item.subItems?.map((si) => isCheckedRecursively(si, selectedFilters)) ??
      [],
  };
};

export const FilterTrayItemsMemo = memo(
  FilterTrayItems,
  <T extends FilterTypes>(
    oldProps: FilterTrayItemsProps<T>,
    newProps: FilterTrayItemsProps<T>
  ) => {
    const checked = isEqual(
      oldProps.items.map((item) =>
        isCheckedRecursively(item, oldProps.filterHandle[0].items)
      ),
      newProps.items.map((item) =>
        isCheckedRecursively(item, newProps.filterHandle[0].items)
      )
    );
    return (
      checked &&
      isEqual(
        oldProps.items.map((i) => i.key),
        newProps.items.map((i) => i.key)
      )
    );
  }
) as typeof FilterTrayItems;
FilterTrayItemsMemo.displayName = 'FilterTrayItemsMemo';
